/**
 * AdminRibbon — amber system bar shown under the header on every page while
 * Admin mode is ON (redesign item 2). Hosts the editorial/system actions that
 * used to live in the reader UI ("Actions:" bar). Amber = admin, never teal.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Brain, Globe } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../AuthContext";
import { useAdminMode } from "../hooks/useAdminMode";

const CHAIN_DOT = {
  ok:      { color: "#22c55e", label: "Autopilot chain OK" },
  running: { color: "#f59e0b", label: "Autopilot step running…" },
  halted:  { color: "#f59e0b", label: "Autopilot halted by a rejection" },
  failed:  { color: "#ef4444", label: "Autopilot step FAILED" },
  idle:    { color: "#9ca3af", label: "No autopilot run this week" },
};

export default function AdminRibbon({ dm = false }) {
  const { authHeader } = useAuth() || {};
  const [, setAdminMode] = useAdminMode();
  const [busy, setBusy] = useState({ refresh: false, perplexity: false, discovery: false });
  const [chain, setChain] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      axios.get(`${API}/admin/full-autopilot/beacon`, { headers: authHeader() })
        .then(({ data }) => { if (alive) setChain(data); })
        .catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setB = (k, v) => setBusy((p) => ({ ...p, [k]: v }));

  const refreshRss = async () => {
    setB("refresh", true);
    try {
      await axios.post(`${API}/news/refresh`, {}, { headers: authHeader() });
      window.dispatchEvent(new Event("ti-feed-reload"));
      toast.success("RSS feeds refreshed");
    } catch { toast.error("RSS refresh failed"); }
    finally { setB("refresh", false); }
  };

  const perplexitySearch = async () => {
    setB("perplexity", true);
    try {
      const res = await axios.post(`${API}/news/perplexity-search`, {}, { headers: authHeader() });
      if (res.data.success) {
        const n = (res.data.articles || []).map(a => ({ ...a, id: `perplexity-${Date.now()}-${Math.random()}` }));
        window.dispatchEvent(new CustomEvent("ti-merge-articles", { detail: { articles: n } }));
        toast.success(`${n.length} articles via Perplexity AI`);
      }
    } catch { toast.error("Perplexity search failed"); }
    finally { setB("perplexity", false); }
  };

  const massiveDiscovery = async () => {
    setB("discovery", true);
    try {
      const res = await axios.post(`${API}/news/massive-discovery`, {}, { headers: authHeader() });
      if (res.data.success && res.data.articles) {
        const n = res.data.articles.map(a => ({ ...a, id: `massive-${Date.now()}-${Math.random()}` }));
        window.dispatchEvent(new CustomEvent("ti-merge-articles", { detail: { articles: n } }));
        toast.success(`Added ${n.length} articles via Massive Discovery!`);
      }
    } catch (e) { toast.error("Discovery failed", { description: e.message }); }
    finally { setB("discovery", false); }
  };

  const actionCls = `flex items-center gap-1.5 text-[11px] font-semibold tracking-wide transition-colors disabled:opacity-50 ${
    dm ? "text-amber-300 hover:text-amber-200" : "text-[#7a5a14] hover:text-[#8a5a06]"
  }`;

  return (
    <div
      data-testid="admin-ribbon"
      className="border-t"
      style={{
        background: dm ? "rgba(216,169,64,0.08)" : "#fdf6e8",
        borderTop: dm ? "1px solid rgba(216,169,64,0.25)" : "1px solid #eeddb5",
        borderBottom: dm ? "1px solid rgba(216,169,64,0.25)" : "1px solid #eeddb5",
      }}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-1.5 flex items-center gap-4 overflow-x-auto no-scrollbar">
        <span
          className="flex-shrink-0 text-[10px] font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full text-white"
          style={{ background: "#8a5a06" }}
        >
          Admin mode
        </span>

        {chain && (() => {
          const h = chain.chain_health || {};
          const dot = CHAIN_DOT[h.state] || CHAIN_DOT.idle;
          const detail = h.state === "failed"
            ? `Failed: ${(h.failed_steps || []).join(", ")}`
            : h.state === "running"
            ? `Running: ${h.running_step}`
            : h.state === "halted"
            ? `Rejected: ${(h.rejected_steps || []).join(", ")}`
            : `Week ${chain.week_id} — ${chain.run ? chain.run.status : "no run"}`;
          return (
            <Link
              to="/admin"
              data-testid="admin-ribbon-autopilot-badge"
              title={`${dot.label} · ${detail}`}
              className={`flex-shrink-0 flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                dm ? "text-amber-200 hover:bg-amber-400/10" : "text-[#7a5a14] hover:bg-[#f7ecd2]"
              }`}
            >
              <span
                data-testid="admin-ribbon-autopilot-dot"
                className={`w-2.5 h-2.5 rounded-full inline-block ${h.state === "running" ? "animate-pulse" : ""}`}
                style={{ background: dot.color, boxShadow: `0 0 6px ${dot.color}` }}
              />
              Autopilot
            </Link>
          );
        })()}

        <button data-testid="admin-ribbon-refresh" onClick={refreshRss} disabled={busy.refresh} title="Refresh RSS feeds" className={actionCls}>
          <RefreshCw className={`w-3 h-3 ${busy.refresh ? "animate-spin" : ""}`} />
          {busy.refresh ? "Refreshing…" : "Refresh RSS"}
        </button>
        <button data-testid="admin-ribbon-discovery" onClick={massiveDiscovery} disabled={busy.discovery} title="Massive discovery sweep" className={actionCls}>
          <Globe className={`w-3 h-3 ${busy.discovery ? "animate-spin" : ""}`} />
          {busy.discovery ? "Sweeping…" : "Discovery sweep"}
        </button>
        <button data-testid="admin-ribbon-perplexity" onClick={perplexitySearch} disabled={busy.perplexity} title="Perplexity AI search" className={actionCls}>
          <Brain className={`w-3 h-3 ${busy.perplexity ? "animate-spin" : ""}`} />
          {busy.perplexity ? "Searching…" : "Perplexity search"}
        </button>
        <Link data-testid="admin-ribbon-console" to="/admin" className={actionCls}>
          Console →
        </Link>

        <span className={`ml-auto flex-shrink-0 hidden sm:inline text-[10.5px] ${dm ? "text-amber-300/60" : "text-[#a08340]"}`}>
          Editorial tools visible on cards
        </span>
        <button
          data-testid="admin-ribbon-exit"
          onClick={() => setAdminMode(false)}
          className={`flex-shrink-0 text-[11px] font-semibold px-3 py-1 rounded-full border transition-colors ${
            dm ? "border-amber-400/40 text-amber-300 hover:bg-amber-400/10" : "border-[#d8a940] text-[#7a5a14] hover:bg-[#f7ecd2]"
          }`}
        >
          Exit admin mode
        </button>
      </div>
    </div>
  );
}
